import { useMemo, useRef, useEffect } from 'react';
import { trackEvent } from '../analytics';
import { getSteps } from './gettingStartedSteps';

export default function GettingStarted({ projects, onNew, onNavigate, onOpen, onDismiss }) {
  const steps = useMemo(() => getSteps({ onNew, onOpen, onNavigate }), [onNew, onOpen, onNavigate]);

  const completionState = useMemo(() => {
    return steps.map(step => ({
      ...step,
      complete: step.detectComplete(projects),
    }));
  }, [steps, projects]);

  const completedCount = completionState.filter(s => s.complete).length;
  const firstIncompleteIdx = completionState.findIndex(s => !s.complete);
  const firstProject = projects[0] || null;

  // Track step completions — fire once per step
  const trackedRef = useRef(new Set());
  useEffect(() => {
    for (const s of completionState) {
      if (s.complete && !trackedRef.current.has(s.id)) {
        trackedRef.current.add(s.id);
        trackEvent("onboarding_step_completed", { step: s.id });
      }
    }
    if (completedCount === steps.length && !trackedRef.current.has("__all__")) {
      trackedRef.current.add("__all__");
      trackEvent("onboarding_all_complete", {});
    }
  }, [completionState, completedCount, steps.length]);

  function handleAction(step) {
    trackEvent("onboarding_step_clicked", { step: step.id });
    if (step.id === "create-project") {
      step.action();
    } else {
      step.action(firstProject);
    }
  }

  function handleDismiss() {
    trackEvent("onboarding_dismissed", { stepsCompleted: completedCount, totalSteps: steps.length });
    onDismiss();
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-stone-800">Getting Started</h2>
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-600 text-sm"
        >
          Skip for now ×
        </button>
      </div>

      <div className="space-y-1">
        {completionState.map((step, idx) => {
          const isComplete = step.complete;
          const isActive = idx === firstIncompleteIdx;
          const isFuture = !isComplete && !isActive;

          return (
            <div
              key={step.id}
              className={`flex items-start gap-3 p-3 rounded-lg ${
                isActive ? "border-l-4 border-[#0f2d5c] bg-slate-50" : ""
              }`}
            >
              {/* Checkmark / circle */}
              <div className="flex-shrink-0 mt-0.5">
                {isComplete ? (
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                ) : (
                  <div className={`w-6 h-6 rounded-full border-2 ${
                    isActive ? "border-[#0f2d5c]" : "border-slate-300"
                  }`} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${
                  isComplete ? "line-through text-slate-400" : "text-stone-800"
                }`}>
                  {step.title}
                </p>
                <p className={`text-xs mt-0.5 ${
                  isComplete ? "text-emerald-600" : isFuture ? "text-slate-400" : "text-slate-500"
                }`}>
                  {isComplete ? step.payoff(projects) : step.why}
                </p>
              </div>

              {/* Action button — only on active step */}
              {isActive && (
                <button
                  onClick={() => handleAction(step)}
                  className="flex-shrink-0 bg-[#e8a020] hover:bg-[#d4911c] text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                >
                  {step.actionLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="mt-5">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
          <span>{completedCount} of {steps.length} complete</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2">
          <div
            className="bg-[#0f2d5c] h-2 rounded-full transition-all"
            style={{ width: `${(completedCount / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

